/**
 * Source detail — single inbox row with R3 (quotes) + R4 (assign) panels.
 * Mobile-first single column. The "Open original" button hands off to the
 * browser; users come back to this screen to paste highlights.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ExternalLink, Quote, Send, Trash2, X,
} from 'lucide-react';
import { api, type InboxRow, type Production, type QuoteRow, type Channel } from '../lib/api';

export function SourceDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [row, setRow] = useState<InboxRow | null>(null);
  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showQuote, setShowQuote] = useState(false);
  const [showAssign, setShowAssign] = useState(false);

  async function refresh() {
    if (!id) return;
    setErr(null);
    try {
      const [r, qs] = await Promise.all([
        api.get<InboxRow>(`/research/inbox/${id}`),
        api.get<QuoteRow[]>(`/research/inbox/${id}/quotes`),
      ]);
      setRow(r);
      setQuotes(qs);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => { void refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  if (!id) return null;
  if (err && !row) return (
    <div className="px-4 py-8 space-y-3">
      <p className="text-danger text-sm break-all">{err}</p>
      <button onClick={() => nav(-1)} className="btn-ghost">Back</button>
    </div>
  );
  if (!row) return <p className="px-4 py-8 text-muted">Loading…</p>;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-10 bg-bg/95 backdrop-blur border-b border-border px-2 py-3 flex items-center gap-2">
        <button onClick={() => nav(-1)} className="tap p-2" aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="font-serif text-lg flex-1 truncate">{row.title ?? hostnameOf(row.url)}</h1>
        <a href={row.url} target="_blank" rel="noopener noreferrer" className="tap p-2" aria-label="Open original">
          <ExternalLink className="h-5 w-5" />
        </a>
      </header>

      <main className="flex-1 px-4 py-4 space-y-4">
        {/* Source preview */}
        <section className="card space-y-3">
          {row.ogImage && (
            <img
              src={row.ogImage}
              alt=""
              className="w-full rounded-lg max-h-48 object-cover bg-border"
              loading="lazy"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          <div>
            <p className="text-xs text-muted mb-1">{row.ogSitename ?? hostnameOf(row.url)}</p>
            <h2 className="font-serif text-lg leading-tight">{row.title ?? row.url}</h2>
            {row.ogDescription && (
              <p className="text-sm text-muted mt-2 leading-relaxed">{row.ogDescription}</p>
            )}
          </div>
          <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary w-full py-3"
          >
            <ExternalLink className="h-4 w-4" />
            Open in browser
          </a>
          <p className="text-xs text-muted break-all">{row.url}</p>
        </section>

        {/* Quotes */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-medium flex items-center gap-2">
              <Quote className="h-4 w-4" /> Quotes ({quotes.length})
            </h3>
            <button onClick={() => setShowQuote(true)} className="btn-ghost py-1 px-3 text-sm">+ Add</button>
          </div>
          {quotes.length === 0 ? (
            <p className="text-muted text-sm">No quotes yet. Highlight in the browser, copy, come back here.</p>
          ) : (
            <div className="space-y-2">
              {quotes.map((q) => <QuoteCard key={q.id} quote={q} onDelete={refresh} />)}
            </div>
          )}
        </section>

        {/* Assign */}
        <section className="space-y-2">
          <h3 className="font-medium flex items-center gap-2">
            <Send className="h-4 w-4" /> Assign
          </h3>
          {row.state === 'assigned' ? (
            <p className="text-sm text-muted">
              Assigned to production <code className="text-accent">{row.assignedProductionId}</code>.
              Visit the main dashboard to edit.
            </p>
          ) : (
            <button onClick={() => setShowAssign(true)} className="btn-primary w-full py-3">
              Assign to a production
            </button>
          )}
        </section>
      </main>

      {showQuote && <AddQuoteDialog inboxId={id} onClose={() => setShowQuote(false)} onAdded={() => { setShowQuote(false); void refresh(); }} />}
      {showAssign && <AssignDialog inboxId={id} onClose={() => setShowAssign(false)} onAssigned={() => { setShowAssign(false); void refresh(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function QuoteCard({ quote, onDelete }: { quote: QuoteRow; onDelete: () => void }) {
  const [busy, setBusy] = useState(false);
  async function remove() {
    if (!confirm('Delete this quote?')) return;
    setBusy(true);
    try {
      await api.delete(`/research/quotes/${quote.id}`);
      onDelete();
    } finally { setBusy(false); }
  }
  return (
    <div className="card space-y-2">
      <blockquote className="border-l-2 border-accent pl-3 text-sm leading-relaxed text-text">
        {quote.quoteText}
      </blockquote>
      {quote.note && (
        <p className="text-xs text-muted italic">{quote.note}</p>
      )}
      <div className="flex justify-end">
        <button onClick={remove} disabled={busy} className="tap p-1" aria-label="Delete quote">
          <Trash2 className="h-4 w-4 text-muted" />
        </button>
      </div>
    </div>
  );
}

function AddQuoteDialog({ inboxId, onClose, onAdded }: { inboxId: string; onClose: () => void; onAdded: () => void }) {
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/research/inbox/${inboxId}/quotes`, {
        quoteText: text.trim(),
        note: note.trim() || undefined,
      });
      onAdded();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally { setBusy(false); }
  }

  return (
    <DialogShell onClose={onClose} title="Add quote">
      <form onSubmit={submit} className="space-y-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste the highlighted passage…"
          rows={6}
          autoFocus
          className="input py-2"
        />
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional)"
          className="input py-3"
        />
        {err && <p className="text-sm text-danger break-all">{err}</p>}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="btn-ghost flex-1 py-3">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary flex-1 py-3">
            {busy ? 'Saving…' : 'Save quote'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

function AssignDialog({ inboxId, onClose, onAssigned }: { inboxId: string; onClose: () => void; onAssigned: () => void }) {
  const [productions, setProductions] = useState<Production[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [pid, setPid] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([
      api.get<Production[]>('/productions'),
      api.get<Channel[]>('/channels'),
    ]).then(([ps, cs]) => {
      setProductions(ps);
      setChannels(cs);
      if (ps[0]) setPid(ps[0].id);
    }).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);

  async function submit() {
    if (!pid) return;
    setErr(null);
    setBusy(true);
    try {
      await api.post(`/research/inbox/${inboxId}/assign`, { productionId: pid });
      onAssigned();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally { setBusy(false); }
  }

  const channelName = (cid: string) => channels.find((c) => c.id === cid)?.name ?? cid;

  return (
    <DialogShell onClose={onClose} title="Assign to production">
      <div className="space-y-3">
        {productions.length === 0 ? (
          <p className="text-muted text-sm">No productions found. Create one on the main dashboard first.</p>
        ) : (
          <select
            value={pid}
            onChange={(e) => setPid(e.target.value)}
            className="input py-3"
          >
            {productions.map((p) => (
              <option key={p.id} value={p.id}>
                {channelName(p.channelId)} · {p.topic.slice(0, 60)}
              </option>
            ))}
          </select>
        )}
        {err && <p className="text-sm text-danger break-all">{err}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-ghost flex-1 py-3">Cancel</button>
          <button onClick={submit} disabled={busy || !pid} className="btn-primary flex-1 py-3">
            {busy ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </DialogShell>
  );
}

function DialogShell({ onClose, title, children }: {
  onClose: () => void; title: string; children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md card rounded-t-2xl rounded-b-none m-0 space-y-3"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-lg">{title}</h2>
          <button onClick={onClose} className="tap p-1" aria-label="Close">
            <X className="h-5 w-5 text-muted" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function hostnameOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return url; }
}
